// Maps questionnaire answers through the field-binding table into a patient
// record partial + touchedFieldPaths — the shared write path for manual entry
// (this phase) and call-answer ingest (Phase 3). Callers pass only answers for
// currently-visible questions (the evaluator's orphan rule).

import {
  transformQuestionnaireAnswerV1,
  type ClinicianWorkbenchPatientRecordV1,
  type ClinicianWorkbenchQuestionnaireFieldBinding,
} from "@amplecare/contracts";

export interface QuestionnaireRecordUpsert {
  record: Record<string, unknown>;
  touchedFieldPaths: string[];
  /** Answered keys with no binding — surfaced, never silently dropped. */
  skippedAnswers: string[];
  /** Answers the transform could not interpret ("about six feet" for a number
   *  field). Writing the resulting null would pass validation as a "clear"
   *  and lose the answer without a trace, so they are withheld and reported. */
  unparsedAnswers: { questionKey: string; verbatim: string }[];
  /** Which question(s) wrote each touched path, so a validation failure — which
   *  names a field path, not a question — can be shown on the question the
   *  nurse actually typed into. Several questions share one path (three feed
   *  basicHistory.smoking.detail), so this is a list: attributing a shared-slot
   *  failure to one of them would point at the wrong question. */
  fieldPathOwners: Record<string, string[]>;
}

function getByPath(
  root: Record<string, unknown>,
  fieldPath: string,
): unknown {
  return fieldPath.split(".").reduce<unknown>((cursor, segment) => {
    if (cursor == null || typeof cursor !== "object") return undefined;
    return (cursor as Record<string, unknown>)[segment];
  }, root);
}

function setByPath(
  root: Record<string, unknown>,
  fieldPath: string,
  value: unknown,
): void {
  const segments = fieldPath.split(".");
  let cursor: Record<string, unknown> = root;
  for (const segment of segments.slice(0, -1)) {
    if (cursor[segment] == null || typeof cursor[segment] !== "object") {
      cursor[segment] = {};
    }
    cursor = cursor[segment] as Record<string, unknown>;
  }
  cursor[segments[segments.length - 1]] = value;
}

function resolveQuestionnaireProjectionMetaPath(fieldPath: string): string {
  const parts = fieldPath.split(".");

  return parts[1] === "basicHistory" && parts.length === 4
    ? parts.slice(0, 3).join(".")
    : fieldPath;
}

export function filterQuestionnaireProjectionClearKeys(input: {
  record: ClinicianWorkbenchPatientRecordV1;
  bindings: ClinicianWorkbenchQuestionnaireFieldBinding[];
  questionKeys: readonly string[];
}): string[] {
  const bindingByKey = new Map(
    input.bindings.map((binding) => [binding.questionKey, binding]),
  );

  return input.questionKeys.filter((questionKey) => {
    const binding = bindingByKey.get(questionKey);

    if (!binding) {
      return true;
    }

    const metaPath = resolveQuestionnaireProjectionMetaPath(binding.fieldPath);
    const meta = input.record.fieldMeta[metaPath];

    // No provenance recorded is not evidence the questionnaire wrote it, so
    // this still refuses — matching the previous behaviour, where an absent
    // fieldMeta failed the updatedBy comparison. The difference now is that the
    // refusal is shown on the question instead of vanishing into a count.
    if (!meta) {
      return false;
    }

    // The questionnaire may clear what the questionnaire wrote — whichever
    // writer produced it. Testing updatedBy against the single literal
    // "questionnaire-projection" only ever matched the projection path: call
    // ingest stamps its attempt id, seeds stamp the agent or clinician id, and
    // a manual save stamps the coordinator label. The form therefore could not
    // clear its OWN saves, every refused clear counted as an unfixable issue,
    // and the section could never be saved again. `source` is the field that
    // actually records what kind of writer this was, and it keeps updatedBy
    // free to answer "who", which the audit trail still needs.
    return meta.source === "questionnaire";
  });
}

export function buildQuestionnaireRecordUpsert(
  answers: Record<string, unknown>,
  bindings: ClinicianWorkbenchQuestionnaireFieldBinding[],
  options: { clearQuestionKeys?: readonly string[] } = {},
): QuestionnaireRecordUpsert {
  const bindingByKey = new Map(
    bindings.map((binding) => [binding.questionKey, binding]),
  );
  const record: Record<string, unknown> = {};
  const touchedFieldPaths: string[] = [];
  const skippedAnswers: string[] = [];
  const unparsedAnswers: { questionKey: string; verbatim: string }[] = [];
  const fieldPathOwners: Record<string, string[]> = {};

  const claimFieldPath = (fieldPath: string, questionKey: string): void => {
    const owners = fieldPathOwners[fieldPath];
    if (owners) {
      if (!owners.includes(questionKey)) owners.push(questionKey);
      return;
    }
    fieldPathOwners[fieldPath] = [questionKey];
  };

  for (const [questionKey, rawValue] of Object.entries(answers)) {
    if (rawValue === undefined || rawValue === null || rawValue === "") continue;
    const binding = bindingByKey.get(questionKey);
    if (!binding) {
      skippedAnswers.push(questionKey);
      continue;
    }
    let value = transformQuestionnaireAnswerV1(
      rawValue,
      binding.transform ?? "verbatim",
    );
    if (value === null || value === undefined) {
      unparsedAnswers.push({ questionKey, verbatim: String(rawValue) });
      continue;
    }
    // Shared text slots (e.g. several detail questions feeding one
    // basicHistory .detail): append instead of clobbering, so no answer is
    // lost to write order.
    if (touchedFieldPaths.includes(binding.fieldPath)) {
      const existing = getByPath(record, binding.fieldPath);
      if (typeof existing === "string" && typeof value === "string" && existing !== value) {
        value = `${existing}; ${value}`;
      }
    }
    setByPath(record, binding.fieldPath, value);
    claimFieldPath(binding.fieldPath, questionKey);
    if (!touchedFieldPaths.includes(binding.fieldPath)) {
      touchedFieldPaths.push(binding.fieldPath);
    }
  }

  for (const questionKey of options.clearQuestionKeys ?? []) {
    const binding = bindingByKey.get(questionKey);

    if (!binding) {
      if (!skippedAnswers.includes(questionKey)) {
        skippedAnswers.push(questionKey);
      }
      continue;
    }

    if (touchedFieldPaths.includes(binding.fieldPath)) {
      continue;
    }

    setByPath(record, binding.fieldPath, null);
    claimFieldPath(binding.fieldPath, questionKey);
    touchedFieldPaths.push(binding.fieldPath);
  }

  return {
    record,
    touchedFieldPaths,
    skippedAnswers,
    unparsedAnswers,
    fieldPathOwners,
  };
}
